import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Update, GetById, GetEmployeeList } from "adminApi/GrandLoanApi/GrandLoanApi";
import { validateForm } from "utils/validation";
import { GrandLoanRules } from "validation/ValidationRules";
import Spinner from "Common/Globle/Spinner";
import EditGrandLoanForm from "./components/EditGrandLoanForm";
import fields from "../json/GrandLoan.json";
import useFormChange from "Hooks/useFormChange";
import useFormSubmit from "Hooks/useFormSubmit";
import useFetchFormDataById from "Hooks/useFetchFormDataById";
import useFetchAllData from "Hooks/useFetchAllData";

const buildPayload = (formValues) => ({
  employee_id: formValues.employee_id,
  permitted_by: formValues.permitted_by,
  loan_details: formValues.loan_details,
  approve_date: formValues.approve_date,
  repayment_from: formValues.repayment_from,
  total_amount: formValues.total_amount,
  interest_percentage: formValues.interest_percentage,
  installment_period_month: formValues.installment_period_month,
  status: formValues.status,
});

const EditGrandLoan = () => {
  const navigate = useNavigate();
  const { formdata } = fields;
  const { id } = useParams();

  // Initialize form values from JSON fields
  const initialValues = formdata.reduce(
    (acc, field) => ({ ...acc, [field.name]: "" }),
    {},
  );

  const { values, errors, handleChange, setErrors, setValues } = useFormChange(
    initialValues,
    GrandLoanRules,
  );

  // Fetch existing data by ID and prepopulate form
  const { loading: loadingFetch } = useFetchFormDataById(
    id,
    formdata,
    GetById,
    setValues,
  );

  const { data: employeeOptions, loading: loadingEmployees } =
    useFetchAllData(GetEmployeeList);

  // Calculate repayment values in UI
  useEffect(() => {
    const principal = parseFloat(values.total_amount);
    const interest = parseFloat(values.interest_percentage);
    const months = parseInt(values.installment_period_month, 10);

    if (
      Number.isNaN(principal) ||
      Number.isNaN(interest) ||
      Number.isNaN(months) ||
      months <= 0
    ) {
      setValues((prev) => {
        if (!prev.repayment_total && !prev.installment) return prev;
        return { ...prev, repayment_total: "", installment: "" };
      });
      return;
    }

    const repaymentTotal = (principal + (principal * interest) / 100).toFixed(2);
    const installment = (Number(repaymentTotal) / months).toFixed(2);

    setValues((prev) => {
      if (
        prev.repayment_total === repaymentTotal &&
        prev.installment === installment
      ) {
        return prev;
      }
      return { ...prev, repayment_total: repaymentTotal, installment };
    });
  }, [
    values.total_amount,
    values.interest_percentage,
    values.installment_period_month,
    setValues,
  ]);

  // Form submit
  const { loading: loadingSubmit, handleSubmit } = useFormSubmit(
    async (formValues) => {
      return await Update({ id, data: buildPayload(formValues) });
    },
    (vals) => {
      const err = validateForm(vals, GrandLoanRules);
      setErrors(err);
      return err;
    },
    () => navigate(-1),
    "Loan updated successfully",
  );

  return (
    <>
      {(loadingSubmit || loadingFetch || loadingEmployees) && (
        <Spinner isSpinner={true} />
      )}
      <EditGrandLoanForm
        values={values}
        errors={errors}
        handleChange={handleChange}
        handleSubmit={(e) => handleSubmit(e, values)}
        loading={loadingSubmit}
        employeeOptions={employeeOptions}
      />
    </>
  );
};

export default EditGrandLoan;
